import { usePreviousValue } from "PFCore/hooks/use_previous_value";
import { Activity, Id, Match, Shortlist } from "PFTypes";
import { useRef } from "react";
import { Transition } from "react-transition-group";

import { MatchCardProfileData } from "./match_card_context.types";
import { MatchCardContextProvider } from "./match_card_context_provider";
import { AnimatedMatchCard } from "./parts/animated_match_card";

type MatchCardProps = {
  role: Activity;
  isCompressed?: boolean;
  isUnbiasedMode?: boolean;
  isFillDisabled?: boolean;
  areButtonsDisabled?: boolean;
  isProfileSelectedToCompare?: boolean;
  isOnMatchesTab?: boolean;
  addProfileToComparison?: (id: Id) => void;
  removeProfileFromComparison?: (id: Id) => void;
  classes?: {
    card?: string;
  };
} & ConditionalMatchCardProps;

type ConditionalMatchCardProps =
  | {
      match: Match;
      shortlist?: never;
    }
  | {
      shortlist: Shortlist;
      match?: Match;
    };

export const MatchCard = ({
  isUnbiasedMode,
  match,
  shortlist,
  isCompressed = false,
  isFillDisabled = false,
  areButtonsDisabled = false,
  isProfileSelectedToCompare = false,
  isOnMatchesTab = false,
  classes,
  ...cardProps
}: MatchCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const wasCompressed = usePreviousValue(isCompressed);

  const parsedMatch: MatchCardProfileData = {
    id: match?.id ?? shortlist!.profile.id,
    profile: match?.profile ?? shortlist!.profile,
    matchedBy: match?.matchedBy || [],
    customValues: match?.customValues ?? { direct: [], indirect: [], related: [], parent: [], child: [] },
    scores: match?.scores ?? {
      score: null,
      direct: null,
      indirect: null,
      related: null,
      normalizedScore: null,
      normalizedDirect: null,
      normalizedIndirect: null,
      normalizedRelated: null,
      availabilityScore: null
    },
    shortlist: shortlist || match?.shortlist,
    creator: shortlist?.creator,
    optionalFields: match?.optionalFields || shortlist?.optionalFields
  };

  return (
    <Transition nodeRef={ref} in={isCompressed} timeout={300}>
      {(state) => (
        <MatchCardContextProvider
          {...parsedMatch}
          isCompressed={
            state === "exiting" || state === "entering" ? wasCompressed || undefined : isCompressed
          }
          isUnbiasedMode={isUnbiasedMode}
          isFillDisabled={isFillDisabled}
          areButtonsDisabled={areButtonsDisabled}
          isShortlistTabActive={!!shortlist}
          isOnMatchesTab={isOnMatchesTab}
          isProfileSelectedToCompare={isProfileSelectedToCompare}
          {...cardProps}
        >
          <AnimatedMatchCard ref={ref} state={state} classes={classes} />
        </MatchCardContextProvider>
      )}
    </Transition>
  );
};
